import { useCallback, useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import { DateTime } from "luxon"

import type { DateRange, DateRangeDateTime } from "types/date.types"

import { DATE_RANGE_TIME } from "helpers/utils/constants"
import { getRangeFromReference } from "widgets/dates/helpers"
import useNow from "./useNow"
import useSelectOptions from "./useSelectOptions"

export const ACT_RANGE_PARAM = "actRange"
export const ACT_FROM_PARAM = "actFrom"
export const ACT_TO_PARAM = "actTo"

const CUSTOM_RANGE = "custom"
const DEFAULT_ACT_RANGE = "lastWeek"

const isValidCustomRange = (from: string | null, to: string | null) => {
  if (!from || !to) return false
  const fromDate = DateTime.fromISO(from)
  const toDate = DateTime.fromISO(to)
  return fromDate.isValid && toDate.isValid && fromDate <= toDate
}

/**
 * Date range for the Alerts → Activity sub-tab.
 *
 * Fully independent from the entity page picker:
 * no Redux range, no zoom and no forecast.
 *
 * Named ranges keep only their name in the URL and are recomputed from the
 * clock, refreshing on the same interval as the global picker so the table
 * keeps up to date while the tab stays open. Params that don't resolve to a
 * known range (or a valid `from <= to` custom pair) are ignored.
 */
const useActivityDateRange = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { noForecastDateReferences } = useSelectOptions()

  const actRange = searchParams.get(ACT_RANGE_PARAM)
  const actFrom = searchParams.get(ACT_FROM_PARAM)
  const actTo = searchParams.get(ACT_TO_PARAM)

  const isCustom = actRange === CUSTOM_RANGE && isValidCustomRange(actFrom, actTo)
  const rangeName =
    !isCustom &&
    actRange &&
    noForecastDateReferences[actRange] &&
    actRange !== CUSTOM_RANGE
      ? actRange
      : DEFAULT_ACT_RANGE

  // Named ranges are relative to now, so they are recalculated on an interval
  // to keep the table fresh without touching the URL.
  const now = useNow(DATE_RANGE_TIME, { enabled: !isCustom, resetKey: rangeName })

  const dateRangeDateTime: DateRangeDateTime = useMemo(() => {
    if (isCustom && actFrom && actTo) {
      const toDate = DateTime.fromISO(actTo)
      return {
        range: CUSTOM_RANGE,
        fromDate: DateTime.fromISO(actFrom),
        toDate,
        zoom: false,
        forecastThresholdDate: toDate,
      }
    }
    const { fromDate, toDate } = getRangeFromReference(
      noForecastDateReferences[rangeName],
      now,
    )
    return {
      range: rangeName,
      fromDate,
      toDate,
      zoom: false,
      forecastThresholdDate: toDate,
    }
  }, [isCustom, actFrom, actTo, noForecastDateReferences, rangeName, now])

  const dateRange: DateRange = useMemo(
    () => ({
      range: dateRangeDateTime.range,
      fromDate: String(dateRangeDateTime.fromDate.toISO()),
      toDate: String(dateRangeDateTime.toDate.toISO()),
      zoom: dateRangeDateTime.zoom,
      forecastThresholdDate: String(dateRangeDateTime.forecastThresholdDate.toISO()),
    }),
    [dateRangeDateTime],
  )

  const writeActParams = useCallback(
    (range: string, from?: string, to?: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set(ACT_RANGE_PARAM, range)
          if (from && to) {
            next.set(ACT_FROM_PARAM, from)
            next.set(ACT_TO_PARAM, to)
          } else {
            next.delete(ACT_FROM_PARAM)
            next.delete(ACT_TO_PARAM)
          }
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const setDateReference = useCallback(
    (dateReferenceName: string) => {
      if (dateReferenceName === CUSTOM_RANGE) {
        // Freeze the range currently on screen as the starting custom range
        writeActParams(CUSTOM_RANGE, dateRange.fromDate, dateRange.toDate)
        return
      }
      if (!noForecastDateReferences[dateReferenceName]) return
      writeActParams(dateReferenceName)
    },
    [dateRange, noForecastDateReferences, writeActParams],
  )

  const updateCustomDateRange = useCallback(
    (fields: { [key: string]: DateTime | null }, current: DateRangeDateTime) => {
      const from = String((fields.fromDate ?? current.fromDate).toISO())
      const to = String((fields.toDate ?? current.toDate).toISO())
      writeActParams(CUSTOM_RANGE, from, to)
    },
    [writeActParams],
  )

  return {
    dateRange,
    dateRangeDateTime,
    setDateReference,
    updateCustomDateRange,
  }
}

export default useActivityDateRange
